<template>
  <div :class="['content mb-5', { 'policy-no-subtitle': !pageMeta.subTitle }]">
    <t-sub-nav
      class="mb-5"
      :loading="loading"
      :items="
        $secondaryNav.customerPolicyNavItems(
          $route.params.id,
          hasVirtualLocation(selectedCustomerPolicy)
        )
      "
    />

    <h2 class="mb-3">{{ this.$t("Domains") }}</h2>

    <b-row class="mb-6">
      <b-col>
        <t-data-table
          data-test-id="policies-domains-data-table"
          with-filter
          :filter-placeholder="$t('Search Domains')"
          :table-data="domains"
          :actions="tableActions"
          :loading="loading"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-row-clicked="onRowClick"
          @on-export="onExport"
          @on-import="onImport()"
          @on-checked-filter="onCheckedFilter"
          :searchExports="searchExports"
          @export-headers="exportedHeaders"
          :checkboxFiltersEnabled="checkboxFiltersEnabled"
          :checkboxFilters="checkboxFilters"
          :selectedCheckboxFilters="selectedCheckboxFilters"
        />
      </b-col>
    </b-row>
    <section>
      <t-uploader :dialog="modal" @submitFiles="submit"></t-uploader>
    </section>
  </div>
</template>

<route>
{
  "name": "Customer Allowed & Blocked Domains",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import TUploader from "@/components/TUploader";
import { mapActions, mapGetters, mapMutations } from "vuex";
import policySubPages from "@/mixins/policySubPages";

export default {
  components: {
    TUploader,
  },
  layout: "customer",
  mixins: [policySubPages],
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("categories", ["fetchPolicyCategories"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("customerDomains", [
      "fetchCustomerDomainList",
      "deleteCustomerDomainListEntrys",
      "addBlockAllowed",
    ]),
    ...mapActions("customerPolicies", [
      "fetchCustomerPolicy",
      "patchCustomerPolicyInheritance",
    ]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("fileUpload", ["setDialog", "clearCache"]),

    onAdd(modal) {
      (modal.modalData.policyId = this.selectedCustomerPolicy.id),
        this.createModal({
          ...modal,
          subtitle: this.selectedUser.accountname,
        });

      this.showModal();
    },
    onRowClick(item) {
      this.$router.push(
        `/customer/content_filtering/domains/${item.id}?t=${item.object}&p=${item.policyid}`
      );
    },
    async onCheckedFilter(event) {
      this.selectedCheckboxFilters = event.checkedFilters;
      try {
        await this.patchCustomerPolicyInheritance({
          id: this.selectedUser.id,
          policyId: this.selectedCustomerPolicy.id,
          inheritance: {
            inherit_default_allow_list: event.checkedFilters.includes(
              this.$t("Inherit Allowed Domains from Default Policy")
            ),
            inherit_default_block_list: event.checkedFilters.includes(
              this.$t("Inherit Blocked Domains from Default Policy")
            ),
          },
        });
      } finally {
        this.fetchCustomerDomainList({
          id: this.selectedUser.id,
          policyId: this.$route.params.id,
        });
        this.clearCache();
      }
    },
    onImport() {
      this.setDialog(true);
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "Domains");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No Domains in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    async submit(value) {
      const action = value.domain.split(" ")[0].toLowerCase();
      const policID = parseInt(this.$route.params.id);
      const files = value.files;

      try {
        await this.addBlockAllowed({
          policyId: policID,
          action: action,
          file: files,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Updated"),
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetchCustomerDomainList({
          id: this.selectedUser.id,
          policyId: this.$route.params.id,
        });
        this.clearCache();
      }
    },

    async onDelete(domains) {
      try {
        await this.deleteCustomerDomainListEntrys({
          userId: this.selectedUser.id,
          domains,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: domains.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
  },
  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("customerPolicies", ["selectedCustomerPolicy"]),
    ...mapGetters("categories", ["customerCategories"]),
    ...mapGetters("customerDomains", ["customerDomainList"]),
    ...mapGetters("pageMeta", ["pageMeta"]),
    ...mapGetters("fileUpload", ["modal"]),
  },
  data: function () {
    return {
      loading: true,
      currentHeaders: null,
      showModalInput: false,
      allowedBlocked: false,
      searchExports: ["domain", "object", "policyname"],

      checkboxFiltersEnabled: true,
      checkboxFilters: [
        this.$t("Inherit Blocked Domains from Default Policy"),
        this.$t("Inherit Allowed Domains from Default Policy"),
      ],
      selectedCheckboxFilters: [],

      tableActions: {
        add: {
          enabled: true,
          label: this.$t("Add Domain"),
          actions: [
            {
              text: this.$t("Allow Domain"),
              value: {
                modal: "t-add-domain",
                dataTestId: "modal-add-domain-allow",
                title: this.$t("Allow Domain"),
                modalData: {
                  type: "allow",
                  toPolicy: true,
                },
                props: {
                  size: "lg",
                  okTitle: this.$t("Allow Domain"),
                },
              },
            },
            {
              text: this.$t("Block Domain"),
              value: {
                modal: "t-add-domain",
                dataTestId: "modal-add-domain-block",
                title: this.$t("Block Domain"),
                modalData: {
                  type: "block",
                  toPolicy: true,
                },
                props: {
                  size: "lg",
                  okTitle: this.$t("Block Domain"),
                },
              },
            },
          ],
        },
        export: { enabled: true },
        import: { enabled: true },
        delete: { enabled: true },
      },
      domains: {
        fields: [
          { value: "domain", text: this.$t("Domain"), sortable: true },
          { value: "object", text: this.$t("Status"), sortable: true },
          { value: "subdomains", text: this.$t("Subdomains"), sortable: true },
          {
            value: "log_requests",
            text: this.$t("Log Requests"),
            sortable: true,
          },
          { value: "comment", text: this.$t("Comments"), sortable: true },
        ],
        items: [],
      },
    };
  },
  watch: {
    customerDomainList: {
      handler() {
        this.domains.items = this.customerDomainList;
      },
      immediate: true,
    },
  },

  async created() {
    await Promise.all([
      this.fetchCustomerDomainList({
        id: this.selectedUser.id,
        policyId: this.$route.params.id,
      }),
      this.fetchCustomerPolicy({
        id: this.selectedUser.id,
        policyId: this.$route.params.id,
      }),
    ]);

    if (this.selectedCustomerPolicy.inherit_default_allow_list) {
      this.selectedCheckboxFilters.push(
        this.$t("Inherit Allowed Domains from Default Policy")
      );
    }
    if (this.selectedCustomerPolicy.inherit_default_block_list) {
      this.selectedCheckboxFilters.push(
        this.$t("Inherit Blocked Domains from Default Policy")
      );
    }

    this.setPageTitle(this.selectedCustomerPolicy.name);
    this.setPageSubtitle([this.selectedCustomerPolicy.description]);

    this.loading = false;
  },
};
</script>

<style lang="scss">
.disabled-row {
  opacity: 0.6;
}
</style>
